/**
 * ExportMenu — export session transcript as markdown (copy or download) + report issue
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { Download, Clipboard, FileDown, Bug, Loader2, Check } from 'lucide-react'
import { request } from '@papan/api-client'
import { useToast } from '@papan/core-ui/ui'
import type { MessagePart } from './hooks.js'
import { isCompactionSummary, isSyntheticUserTurn } from './compaction-predicates.js'

interface MessageItem {
  id: string
  role: string
  parts: MessagePart[]
  modelID?: string
  timeCreated?: number
  /** System-written user turn (compaction nudge / overflow replay). */
  synthetic?: boolean
  /** Assistant message that IS a compaction summary. */
  isSummary?: boolean
}

interface Props {
  messages: MessageItem[]
  sessionTitle?: string
  sessionId?: string
  workspaceId?: string
}

export function formatTranscript(messages: MessageItem[]): string {
  const lines: string[] = ['# Session Transcript\n']

  for (const msg of messages) {
    // Compaction's auto-continue nudge and overflow replay are persisted as
    // user turns for the MODEL's benefit. An export is the artifact people
    // paste into bug reports and share, so heading them "## User" attributes
    // to the user words they never said — the same #438 defect as the
    // transcript, on the surface that outlives the session.
    // Both shapes: the message-level flag, and the LEGACY per-part one every
    // auto-continue nudge written before that flag existed still carries.
    // `MessageList` accepts both; accepting only one here means those older
    // rows keep exporting under "## User" with text the user never wrote —
    // on the surface that outlives the session.
    if (msg.role === 'user' && isSyntheticUserTurn(msg)) continue

    const role = msg.role === 'user'
      ? 'User'
      : isCompactionSummary(msg) ? 'Context summary' : 'Assistant'
    lines.push(`## ${role}\n`)

    for (const part of msg.parts) {
      switch (part.type) {
        case 'text':
          lines.push(part.content)
          lines.push('')
          break
        case 'reasoning':
          lines.push(`> *Reasoning:* ${part.content}`)
          lines.push('')
          break
        case 'tool-call':
          lines.push(`**Tool:** \`${part.toolName}\``)
          if (part.input && Object.keys(part.input).length > 0) {
            lines.push('```json')
            lines.push(JSON.stringify(part.input, null, 2))
            lines.push('```')
          }
          lines.push('')
          break
        case 'tool-result':
          if (part.output) {
            lines.push('```')
            lines.push(part.output.slice(0, 2000))
            if (part.output.length > 2000) lines.push('... (truncated)')
            lines.push('```')
          }
          if (part.error) {
            lines.push(`**Error:** ${part.error}`)
          }
          lines.push('')
          break
        case 'compaction':
          lines.push('---')
          lines.push('*Compaction*')
          lines.push('---')
          lines.push('')
          break
      }
    }
  }

  return lines.join('\n')
}

export function ExportMenu({ messages, sessionTitle, sessionId, workspaceId }: Props) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [reportState, setReportState] = useState<'idle' | 'input' | 'submitting' | 'done'>('idle')
  const [reportComment, setReportComment] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)
  const commentRef = useRef<HTMLTextAreaElement>(null)
  const toast = useToast()

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
        if (reportState === 'input') setReportState('idle')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, reportState])

  useEffect(() => {
    if (reportState === 'input' && commentRef.current) {
      commentRef.current.focus()
    }
  }, [reportState])

  const handleReport = useCallback(async () => {
    if (!sessionId || !workspaceId) return
    setReportState('submitting')
    try {
      const result = await request<{
        ok: boolean
        reportPath: string
        messageCount: number
        filesTracked: number
        versionsSaved: number
      }>(`/api/agent/sessions/${sessionId}/report?workspaceId=${workspaceId}`, {
        method: 'POST',
        body: JSON.stringify({ comment: reportComment.trim() || undefined }),
        headers: { 'Content-Type': 'application/json' },
      })
      if (result.ok) {
        setReportState('done')
        toast.success(`Report submitted (${result.messageCount} messages, ${result.filesTracked} files)`)
        setTimeout(() => {
          setOpen(false)
          setReportState('idle')
          setReportComment('')
        }, 1500)
      }
    } catch (err) {
      toast.error('Failed to create report')
      console.error('[Report]', err)
      setReportState('idle')
    }
  }, [sessionId, workspaceId, reportComment, toast])

  if (messages.length === 0) return null

  const handleCopy = async () => {
    const md = formatTranscript(messages)
    await navigator.clipboard.writeText(md)
    setCopied(true)
    setTimeout(() => { setCopied(false); setOpen(false) }, 1000)
  }

  const handleDownload = () => {
    const md = formatTranscript(messages)
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${sessionTitle || 'session'}.md`
    a.click()
    URL.revokeObjectURL(url)
    setOpen(false)
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
        title="Export transcript"
      >
        <Download size={13} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 rounded-md border themed-border themed-bg-secondary shadow-lg z-50">
          <div className="py-1">
            <button
              onClick={handleCopy}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
            >
              <Clipboard size={12} />
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </button>
            <button
              onClick={handleDownload}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
            >
              <FileDown size={12} />
              Download .md
            </button>

            {sessionId && workspaceId && (
              <>
                <div className="border-t themed-border my-1" />
                {reportState === 'idle' && (
                  <button
                    onClick={() => setReportState('input')}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
                  >
                    <Bug size={12} />
                    Report issue
                  </button>
                )}

                {reportState === 'input' && (
                  <div className="px-3 py-2">
                    <textarea
                      ref={commentRef}
                      value={reportComment}
                      onChange={(e) => setReportComment(e.target.value)}
                      placeholder="Describe the issue (optional)"
                      className="w-full h-16 text-xs rounded themed-input px-2 py-1.5 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          handleReport()
                        }
                      }}
                    />
                    <div className="flex gap-1.5 mt-1.5">
                      <button
                        onClick={handleReport}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1 text-xs rounded
                          bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                      >
                        <Bug size={11} />
                        Submit report
                      </button>
                      <button
                        onClick={() => { setReportState('idle'); setReportComment('') }}
                        className="px-2 py-1 text-xs rounded themed-btn-secondary transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {reportState === 'submitting' && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs themed-text-muted">
                    <Loader2 size={12} className="animate-spin" />
                    Creating report...
                  </div>
                )}

                {reportState === 'done' && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-green-400">
                    <Check size={12} />
                    Report submitted
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
